<template>
  <!-- THE CUSTOMER'S DISC, from a NAME. `ContactTag` and `CompanyTag` already
       draw this over a loaded resource; surfaces like the sales desk carry the
       customer as a label the server composed (a person, a company, or both),
       and re-fetching the resource just to find two letters would be a query
       for decoration.
       ⚠ The hue hashes on `identity`, so pass the PERSON key, not the row's —
       one customer wears one colour wherever she appears, which is also what
       makes her two dossiers read as hers. -->
  <Avatar :size="size" shape="circle" :identity="identity" :class="props.class">
    <AvatarFallback>{{ initials }}</AvatarFallback>
  </Avatar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { HTMLAttributes } from 'vue'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import type { AvatarVariants } from '@/components/ui/avatar'

const props = withDefaults(defineProps<{
  name?: string | null
  /** What the colour hashes on — the customer, never the row. */
  identity?: string | null
  size?: AvatarVariants['size']
  class?: HTMLAttributes['class']
}>(), { name: null, identity: null, size: 'xs', class: '' })

// First letter of the first two WORDS — « Benoît Morin » → BM, « Haie Vive
// inc. » → HV. Punctuation-only fragments are skipped so « J.-P. Lafleur »
// does not read as « .. »; nothing legible left is the same « ? » the other
// two tags use.
const initials = computed(() => {
  const letters = (props.name ?? '')
    .split(/[\s]+/)
    .map(word => [ ...word ].find(char => /\p{L}/u.test(char)))
    .filter((char): char is string => Boolean(char))
    .slice(0, 2)
  return letters.length ? letters.join('').toUpperCase() : '?'
})
</script>
